import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from '../../icons';
import { useEscapeClose } from '../../hooks/useEscapeClose';

/**
 * 앵커에 붙는 팝오버 껍데기 (2026-08-14).
 *
 * 행 메뉴·정렬 메뉴·보기 메뉴가 전부 같은 배치 문제를 갖는다 — 목록 영역이 스크롤 컨테이너라
 * 그 안에 절대배치하면 잘리고, 화면 아래쪽 행에서 열면 메뉴가 뷰포트를 넘는다. 세 곳이 각자
 * 풀면 미묘하게 다르게 잘리므로 여기 한 번만 짠다.
 *
 * `WorkspaceCreateMenu` 는 좌측 패널 전용 스타일이 있어 따로 두었지만 배치 규칙은 같다.
 */

const GAP = 6;
const EDGE = 12;

interface Props {
  /** 이 요소 아래(또는 위)에 붙는다. */
  anchor: HTMLElement;
  onClose: () => void;
  /** 오른쪽 끝을 앵커에 맞춘다 — 목록 우측의 ⋯ 버튼처럼 화면 끝에 가까운 앵커용. */
  align?: 'left' | 'right';
  width?: number;
  className?: string;
  ariaLabel: string;
  children: ReactNode;
}

export function WorkspacePopover({
  anchor, onClose, align = 'right', width = 216, className = '', ariaLabel, children,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEscapeClose(onClose);

  // 페인트 전에 자리를 잡는다 — useEffect 로 미루면 (0,0) 에 한 프레임 그려졌다 튄다.
  useLayoutEffect(() => {
    const r = anchor.getBoundingClientRect();
    const h = ref.current?.offsetHeight ?? 320;
    let top = r.bottom + GAP;
    if (top + h > window.innerHeight - EDGE) {
      // 아래로 안 들어가면 위로. 위도 모자라면 화면 안으로 밀어 넣는다.
      top = Math.max(EDGE, Math.min(r.top - GAP - h, window.innerHeight - h - EDGE));
    }
    const rawLeft = align === 'right' ? r.right - width : r.left;
    const left = Math.max(EDGE, Math.min(rawLeft, window.innerWidth - width - EDGE));
    setPos({ top, left });
  }, [anchor, align, width]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor.contains(t)) return;
      onClose();
    }
    // 여는 클릭이 그대로 바깥 클릭으로 잡히지 않도록 한 틱 미룬다.
    const id = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => { window.clearTimeout(id); document.removeEventListener('mousedown', onDown); };
  }, [anchor, onClose]);

  // 스크롤하면 앵커가 움직여 메뉴만 떠 있게 된다 — 그럴 바엔 닫는다.
  useEffect(() => {
    const close = () => onClose();
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      className={`ws-pop ${className}`.trim()}
      role="menu"
      aria-label={ariaLabel}
      style={pos ? { top: pos.top, left: pos.left, width } : { visibility: 'hidden', width }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** 팝오버 안의 항목 한 줄. */
export function PopoverItem({
  icon: Icon, label, onClick, danger = false, checked, radio = false,
}: {
  // ⚠ lucide 아이콘은 forwardRef 컴포넌트라 `(p) => JSX.Element` 로 받으면 대입되지 않는다
  //   (ReactNode 가 Element 보다 넓다). 레포가 이미 re-export 하는 LucideIcon 을 쓴다.
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  danger?: boolean;
  /** 지정하면 radio 로 동작한다(정렬 메뉴). */
  checked?: boolean;
  /**
   * 아이콘이 **체크 표시**인가 (2026-08-18).
   *
   * true 면 안 고른 항목의 아이콘을 감춘다(자리는 남겨 라벨이 안 흔들린다) — 정렬 메뉴처럼
   * 모든 줄이 같은 `Check` 를 쓰는 경우다. 기본은 false: '보기' 메뉴(목록·간략·타일)처럼
   * **아이콘 자체가 뜻**이면 안 골랐어도 보여야 한다. 그전에는 CSS 가 checked=false 전부를
   * 감춰서, 보기 메뉴는 지금 고른 한 줄만 아이콘이 있었다.
   */
  radio?: boolean;
}) {
  return (
    <button
      type="button"
      role={checked === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={checked}
      data-radio={radio ? 'true' : undefined}
      className={`ws-pop-item${danger ? ' ws-pop-item--danger' : ''}`}
      onClick={onClick}
    >
      <Icon size={16} aria-hidden />
      {label}
    </button>
  );
}

export function PopoverSep() {
  return <div className="ws-pop-sep" role="separator" />;
}

export function PopoverLabel({ children }: { children: ReactNode }) {
  return <div className="ws-pop-label">{children}</div>;
}
